import { Divider, MenuItem, Paper, Select, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  getLotLocations,
  selectLotLocationDistance,
  selectLotLocationLocationString,
  setDistance,
  setLocation,
} from "features/lotLocations/lotLocationsSlice";
import { FocusEventHandler, useCallback, useRef, useState } from "react";

export default function LocationFilterSection() {
  const dispatch = useAppDispatch();
  const distance = useAppSelector(selectLotLocationDistance);
  const [location, setLocalLocation] = useState(useAppSelector(selectLotLocationLocationString));
  const [lastFetchedLocation, setLastFetchedLocation] = useState(location);
  // useCallback to memoize the debounced function
  const debouncedSetLocation = useCallback(
    (value: string) => {
      dispatch(setLocation(value));
    },
    [dispatch]
  );

  // useRef to hold the timeout id
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleLocationChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const value = event.target.value;
    setLocalLocation(value);

    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      debouncedSetLocation(value);
    }, 500); // Adjust the delay (in milliseconds) as needed
  };

  const handleLocationBlur: FocusEventHandler = () => {
    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    debouncedSetLocation(location);
    updateLotLocations();
  };

  const handleDistanceChange = (event: any) => {
    if (event.target.value === 0) {
      dispatch(setDistance(undefined));
    }
    else {
      dispatch(setDistance(event.target.value));
    }
    dispatch(getLotLocations());
  };

  const updateLotLocations = () => {
    // Only update if the location has changed
    if (location !== lastFetchedLocation) {
      setLastFetchedLocation(location);
      dispatch(getLotLocations());
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') {
      debouncedSetLocation(location);
      updateLotLocations();
    }
  };

  return (
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "2px 4px",
        borderRadius: "30px",
        width: { xs: "auto", lg: "400px" },
        height: "50px",
        gap: 2,
      }}
    >
      <TextField
        placeholder="Ort oder Postleitzahl"
        variant="standard"
        InputProps={{ disableUnderline: true }}
        value={location}
        onChange={handleLocationChange}
        onBlur={handleLocationBlur}
        onKeyDown={handleKeyDown}
        sx={{
          pl: 2,
          fontSize: "15px",
          width: { xs: "100%", lg: "335px" },
          "&::before": { borderBottom: "none" },
        }}
      />
      <Divider
        sx={{ height: 28, m: 1, color: "#1F2A40", border: 1 }}
        orientation="vertical"
      />

      <Select
        variant="standard"
        value={distance === undefined ? 0 : distance}
        label="Km"
        onChange={handleDistanceChange}
        placeholder="+25"
        disableUnderline
        sx={{
          width: { xs: "auto", lg: "100px" },
          "&::before": { borderBottom: "none" },
        }}
      >
        <MenuItem value={0}> alle</MenuItem>
        <MenuItem value={2}>+ 2km</MenuItem>
        <MenuItem value={5}>+ 5km</MenuItem>
        <MenuItem value={10}>+ 10km</MenuItem>
        <MenuItem value={25}>+ 25km</MenuItem>
        <MenuItem value={50}>+ 50km</MenuItem>
      </Select>
    </Paper>
  );
}
